import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { authService } from '../services/authService';
import { startSessionKeepAlive, setSessionPrefs, normalizeSessionPrefs } from '../lib/sessionKeepAlive';
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import { Layout, Menu, Avatar, Dropdown, Space, Button } from 'antd';
import {
  DashboardOutlined,
  LogoutOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  SafetyOutlined,
  BarChartOutlined,
  ApartmentOutlined,
  MonitorOutlined,
  ClockCircleOutlined,
  FileProtectOutlined,
  MailOutlined,
  DollarOutlined,
  ApiOutlined,
  AppstoreOutlined,
  CustomerServiceOutlined,
  ExperimentOutlined,
  RobotOutlined,
} from '@ant-design/icons';
import { useAppSelector, useAppDispatch } from '../app/hooks';
import { selectUser, selectRoles, logout } from '../features/auth/authSlice';
import { hasPermission } from '../rbac/rbac.generated';
import { KaziPeopleLogo, KaziPeopleIcon } from '../components/KaziPeopleLogo';
import { ThemeToggle } from '../theme/ThemeToggle';
import { useTheme } from '../theme/ThemeProvider';

// A nav entry; `requires` is the permission gating its visibility (omit = always).
type NavItem = {
  key: string;
  icon?: React.ReactNode;
  label: React.ReactNode;
  requires?: string;
  children?: NavItem[];
};

/** Drop entries the user lacks permission for; hide parents left with no children. */
function filterNav(items: NavItem[], can: (perm?: string) => boolean): any[] {
  return items
    .map((it) => {
      if (it.children) {
        const kids = filterNav(it.children, can);
        if (!kids.length) return null;
        const { requires: _r, children: _c, ...rest } = it;
        return { ...rest, children: kids };
      }
      if (!can(it.requires)) return null;
      const { requires: _r, ...rest } = it;
      return rest;
    })
    .filter(Boolean);
}

const { Header, Sider, Content } = Layout;

const AdminLayout: React.FC = () => {
  const [collapsed, setCollapsed] = useState(false);
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const user = useAppSelector(selectUser);

  const handleLogout = () => {
    dispatch(logout());
    navigate('/login');
  };

  // Keep the session alive while the operator is active (per-user preference —
  // {keepActive, idleTimeoutMinutes} from auth-service user preferences, cached
  // locally by the keep-alive module). AdminLayout only mounts signed-in, so no
  // isAuthed gate is needed; on idle timeout, revoke and land on login.
  useEffect(() => {
    const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:8000/api/v1';
    let cancelled = false;
    axios
      .get(`${API_URL}/users/profile`)
      .then((r) => {
        if (!cancelled) setSessionPrefs(normalizeSessionPrefs(r.data?.userMetadata?.session));
      })
      .catch(() => undefined);
    const stop = startSessionKeepAlive({
      refresh: () => authService.refreshToken(),
      signOut: () => {
        void authService.logout().finally(() => window.location.assign('/login?expired=1'));
      },
    });
    return () => {
      cancelled = true;
      stop();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const userMenuItems = [
    {
      key: 'change-password',
      icon: <SafetyOutlined />,
      label: 'Change password',
      onClick: () => navigate('/change-password'),
    },
    {
      type: 'divider' as const,
    },
    {
      key: 'logout',
      icon: <LogoutOutlined />,
      label: 'Logout',
      onClick: handleLogout,
      danger: true,
    },
  ];

  const roles = useAppSelector(selectRoles);
  const can = (perm?: string) => !perm || hasPermission(roles, perm);

  const navItems: NavItem[] = [
    {
      key: '/dashboard',
      icon: <DashboardOutlined />,
      label: <Link to="/dashboard">Overview</Link>,
    },
    {
      key: 'organizations',
      icon: <ApartmentOutlined />,
      label: 'Organizations',
      children: [
        { key: '/tenants', label: <Link to="/tenants">Tenants</Link>, requires: 'tenant:read:all' },
        { key: '/tenants/applications', label: <Link to="/tenants/applications">Applications</Link>, requires: 'tenant:create' },
        { key: '/tenants/onboarding', label: <Link to="/tenants/onboarding">Onboarding</Link>, requires: 'tenant:create' },
      ],
    },
    {
      key: 'partners',
      icon: <AppstoreOutlined />,
      label: 'Partners',
      children: [
        { key: '/credit/partner-network', label: <Link to="/credit/partner-network">Network (leads & commissions)</Link>, requires: 'tenant:read:all' },
        { key: '/credit/partner-applications', label: <Link to="/credit/partner-applications">Applications</Link>, requires: 'tenant:read:all' },
        { key: '/credit/products-review', label: <Link to="/credit/products-review">Products</Link>, requires: 'tenant:read:all' },
        { key: '/credit/partner-capabilities', label: <Link to="/credit/partner-capabilities">Capabilities</Link>, requires: 'tenant:read:all' },
        { key: '/marketplace', label: <Link to="/marketplace">Marketplace governance</Link>, requires: 'tenant:read:all' },
      ],
    },
    {
      key: 'financial',
      icon: <DollarOutlined />,
      label: 'Financial Operations',
      children: [
        { key: '/billing', label: <Link to="/billing">Billing</Link>, requires: 'billing:read:all' },
        { key: '/billing/collection', label: <Link to="/billing/collection">Payment collection</Link>, requires: 'billing:read:all' },
        { key: '/payments', label: <Link to="/payments">Payments</Link>, requires: 'billing:read:all' },
        { key: '/payments/routing', label: <Link to="/payments/routing">Payment routing</Link>, requires: 'tenant:read:all' },
        { key: '/payments/connection-profiles', label: <Link to="/payments/connection-profiles">Bank connections</Link>, requires: 'tenant:read:all' },
        { key: '/payments/accounting', label: <Link to="/payments/accounting">Payout accounting</Link>, requires: 'tenant:read:all' },
        { key: '/payments/ops', label: <Link to="/payments/ops">Payment operations</Link>, requires: 'tenant:read:all' },
        { key: '/everyday-services', label: <Link to="/everyday-services">Everyday Services</Link>, requires: 'commerce:ops' },
        { key: '/marketplace-launch', icon: <AppstoreOutlined />, label: <Link to="/marketplace-launch">Marketplace launch</Link>, requires: 'tenant:read:all' },
      ],
    },
    {
      key: 'platform',
      icon: <ApiOutlined />,
      label: 'Platform',
      children: [
        { key: '/system/monitoring', icon: <MonitorOutlined />, label: <Link to="/system/monitoring">Services & health</Link>, requires: 'monitoring:read' },
        { key: '/system/scheduler', icon: <ClockCircleOutlined />, label: <Link to="/system/scheduler">Jobs & events</Link>, requires: 'integration:manage' },
        { key: '/system/eventbus', icon: <ApiOutlined />, label: <Link to="/system/eventbus">Event bus</Link>, requires: 'monitoring:read' },
        { key: '/system/feature-flags', icon: <ExperimentOutlined />, label: <Link to="/system/feature-flags">Feature flags</Link>, requires: 'feature-flag:manage' },
        { key: '/system/ai-keys', icon: <ExperimentOutlined />, label: <Link to="/system/ai-keys">AI platform keys</Link>, requires: 'feature-flag:manage' },
        { key: '/analytics/growth', icon: <BarChartOutlined />, label: <Link to="/analytics/growth">Growth analytics</Link>, requires: 'analytics:read' },
        { key: '/ai-ops', icon: <RobotOutlined />, label: <Link to="/ai-ops">Dana AI ops</Link>, requires: 'analytics:read' },
        { key: '/developer', label: <Link to="/developer">Developer platform</Link>, requires: 'tenant:read:all' },
        { key: '/developer/connectors', label: <Link to="/developer/connectors">Connector catalog</Link>, requires: 'tenant:read:all' },
        { key: '/developer/connector-incidents', label: <Link to="/developer/connector-incidents">Connector incidents</Link>, requires: 'tenant:read:all' },
        { key: '/developer/sandboxes', label: <Link to="/developer/sandboxes">Sandbox tenants</Link>, requires: 'tenant:create' },
        { key: '/developer/certifications', label: <Link to="/developer/certifications">Certifications</Link>, requires: 'tenant:update:all' },
        { key: '/developer/ai-clients', label: <Link to="/developer/ai-clients">AI clients</Link>, requires: 'tenant:update:all' },
        { key: '/system/deliverability', icon: <MailOutlined />, label: <Link to="/system/deliverability">Deliverability</Link>, requires: 'monitoring:read' },
      ],
    },
    {
      key: 'compliance',
      icon: <FileProtectOutlined />,
      label: 'Countries & Compliance',
      children: [
        { key: '/countries', label: <Link to="/countries">Countries</Link>, requires: 'country_pack:read' },
        { key: '/country-packs', label: <Link to="/country-packs">Statutory packs</Link>, requires: 'country_pack:read' },
        { key: '/holiday-packs', label: <Link to="/holiday-packs">Holiday packs</Link>, requires: 'country_pack:read' },
        { key: '/export-templates', label: <Link to="/export-templates">Export templates</Link>, requires: 'country_pack:read' },
        { key: '/tax-rules', label: <Link to="/tax-rules">Withholding tax rules</Link>, requires: 'tenant:read:all' },
        {
          key: '/compliance/governance',
          label: <Link to="/compliance/governance">Dashboard</Link>,
          requires: 'compliance:dashboard:view',
        },
        {
          key: '/compliance',
          label: <Link to="/compliance">Legal Documents</Link>,
          requires: 'auditlog:read:all',
        },
        {
          key: '/compliance/privacy',
          label: <Link to="/compliance/privacy">Privacy Operations</Link>,
          requires: 'auditlog:read:all',
        },
        {
          key: '/compliance/retention-policies',
          label: <Link to="/compliance/retention-policies">Retention Policies</Link>,
          requires: 'compliance:retention_policy:view',
        },
        {
          key: '/compliance/legal-holds',
          label: <Link to="/compliance/legal-holds">Legal Holds</Link>,
          requires: 'compliance:legal_hold:view',
        },
        {
          key: '/compliance/erasure-requests',
          label: <Link to="/compliance/erasure-requests">Erasure Requests</Link>,
          requires: 'compliance:erasure_request:view',
        },
        {
          key: '/compliance/dry-run',
          label: <Link to="/compliance/dry-run">Retention Dry-Run Review</Link>,
          requires: 'compliance:dry_run:view',
        },
        {
          key: '/compliance/audit-evidence',
          label: <Link to="/compliance/audit-evidence">Audit Evidence</Link>,
          requires: 'compliance:audit_evidence:view',
        },
        {
          key: '/compliance/tenant-lifecycle',
          label: <Link to="/compliance/tenant-lifecycle">Tenant Data Lifecycle</Link>,
          requires: 'tenant_lifecycle:view',
        },
        {
          key: '/compliance/employee-lifecycle',
          label: <Link to="/compliance/employee-lifecycle">Employee Data Lifecycle</Link>,
          requires: 'employee_lifecycle:view',
        },
        {
          key: '/system/audit',
          label: <Link to="/system/audit">Audit log</Link>,
          requires: 'auditlog:read:all',
        },
      ],
    },
    {
      key: 'security',
      icon: <SafetyOutlined />,
      label: 'Security',
      children: [
        { key: '/operators', label: <Link to="/operators">Operators</Link>, requires: 'platform-staff:manage' },
        { key: '/operators/tokens', label: <Link to="/operators/tokens">Operator tokens</Link> },
        { key: '/users', label: <Link to="/users">Users & access</Link>, requires: 'user:read:all' },
        { key: '/roles', label: <Link to="/roles">Roles & permissions</Link>, requires: 'feature-flag:manage' },
        { key: '/system/security', label: <Link to="/system/security">Security posture</Link>, requires: 'monitoring:read' },
        { key: '/security/sessions', label: <Link to="/security/sessions">Active sessions</Link>, requires: 'monitoring:read' },
        { key: '/security/access-changes', label: <Link to="/security/access-changes">Access changes</Link>, requires: 'auditlog:read:all' },
      ],
    },
    {
      key: 'support',
      icon: <CustomerServiceOutlined />,
      label: 'Support',
      children: [
        { key: '/support/cases', label: <Link to="/support/cases">Cases</Link>, requires: 'support:read' },
        { key: '/support/incidents', label: <Link to="/support/incidents">Incidents</Link>, requires: 'incident:read' },
        { key: '/support/announcements', label: <Link to="/support/announcements">Announcements</Link>, requires: 'announcement:manage' },
        { key: '/settings/ticketing', label: <Link to="/settings/ticketing">Ticketing integration</Link>, requires: 'support_integration:manage' },
      ],
    },
    {
      key: '/reports',
      icon: <BarChartOutlined />,
      label: <Link to="/reports">Analytics</Link>,
      requires: 'analytics:read',
    },
  ];

  const sideMenuItems = filterNav(navItems, can);

  return (
    <Layout className="min-h-screen">
      <Sider
        trigger={null}
        collapsible
        collapsed={collapsed}
        theme={isDark ? 'dark' : 'light'}
        className="shadow-md"
        width={250}
      >
        <div className="h-16 flex items-center justify-center border-b border-border">
          {collapsed ? <KaziPeopleIcon size={32} /> : <KaziPeopleLogo size={32} />}
        </div>
        <Menu
          theme={isDark ? 'dark' : 'light'}
          mode="inline"
          selectedKeys={[location.pathname]}
          items={sideMenuItems}
          className="border-r-0"
        />
      </Sider>
      
      <Layout>
        <Header className="bg-card px-4 shadow-sm flex items-center justify-between">
          <Button
            type="text"
            icon={collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
            onClick={() => setCollapsed(!collapsed)}
            className="text-lg"
          />

          <Space size="large">
            <ThemeToggle />
            <Dropdown menu={{ items: userMenuItems }} placement="bottomRight">
              <Space className="cursor-pointer">
                <Avatar
                  size="default"
                  className="bg-primary-500"
                >
                  {user?.name?.charAt(0).toUpperCase() || 'A'}
                </Avatar>
                <div className="hidden md:block">
                  <div className="text-sm font-medium">{user?.name || 'Admin User'}</div>
                  <div className="text-xs text-muted-foreground">{user?.role?.replace('-', ' ').toUpperCase()}</div>
                </div>
              </Space>
            </Dropdown>
          </Space>
        </Header>
        
        <Content className="m-6">
          <div className="bg-card rounded-lg shadow-sm p-6 min-h-full">
            <Outlet />
          </div>
        </Content>
      </Layout>
    </Layout>
  );
};

export default AdminLayout;